/**
 * Conversation Selector Component
 *
 * A dropdown for switching between conversations and creating new ones.
 */
import React from 'react';
import { Plus, History, Pin, Download, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useGlobalAssistant } from '@/contexts/GlobalAssistantContext';
import {
  exportConversation,
  downloadConversation,
  updateConversation,
} from '@/services/conversationApi';

export function ConversationSelector() {
  const {
    currentConversationId,
    conversations,
    conversationsLoading,
    startNewConversation,
    loadConversation,
    loadConversationList,
  } = useGlobalAssistant();

  const handleExport = async (format: 'json' | 'markdown') => {
    if (!currentConversationId) return;
    try {
      const blob = await exportConversation(currentConversationId, format);
      downloadConversation(blob, currentConversationId, format);
    } catch (err) {
      console.error('Failed to export conversation:', err);
    }
  };

  const handleTogglePin = async (conversationId: string, isPinned: boolean) => {
    try {
      await updateConversation(conversationId, { is_pinned: !isPinned });
      loadConversationList();
    } catch (err) {
      console.error('Failed to update conversation:', err);
    }
  };

  // Format relative time
  const formatRelativeTime = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="flex items-center gap-1">
      {/* History dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7" title="Conversation history">
            <History className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuItem onClick={startNewConversation}>
            <Plus className="mr-2 h-4 w-4" />
            New conversation
          </DropdownMenuItem>
          <DropdownMenuSeparator />

          {conversationsLoading ? (
            <DropdownMenuItem disabled className="text-muted-foreground">
              Loading...
            </DropdownMenuItem>
          ) : conversations.length === 0 ? (
            <DropdownMenuItem disabled className="text-muted-foreground">
              No conversations yet
            </DropdownMenuItem>
          ) : (
            conversations.slice(0, 10).map((conv) => (
              <DropdownMenuItem
                key={conv.id}
                onClick={() => loadConversation(conv.id)}
                className={cn(
                  'flex items-center justify-between',
                  conv.id === currentConversationId && 'bg-accent'
                )}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {conv.is_pinned && <Pin className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
                  <span className="truncate">
                    {conv.title || `Chat ${conv.id.slice(0, 8)}`}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">
                  {formatRelativeTime(conv.last_message_at)}
                </span>
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Actions for current conversation */}
      {currentConversationId && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7" title="Conversation options">
              <MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                const conv = conversations.find((c) => c.id === currentConversationId);
                if (conv) handleTogglePin(conv.id, conv.is_pinned);
              }}
            >
              <Pin className="mr-2 h-4 w-4" />
              {conversations.find((c) => c.id === currentConversationId)?.is_pinned
                ? 'Unpin'
                : 'Pin conversation'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleExport('json')}>
              <Download className="mr-2 h-4 w-4" />
              Export as JSON
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('markdown')}>
              <Download className="mr-2 h-4 w-4" />
              Export as Markdown
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
